import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PillLink } from '@/components/generals/pill-link'
import { SectionLabel } from '@/components/generals/section-label'
import Cta from '@/components/landing/cta'

import { company } from '@/constants/site'
import {
    benefits,
    benefitsTitle,
    services,
    servicesIntro
} from '@/constants/home'

const Services: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Serviços'
                description="Desembaraço aduaneiro, assessoria em importação e exportação, logística internacional e consultoria especializada: atuação completa em comércio exterior com a Ativa."
            />

            <section
                data-header-theme='dark'
                className='relative isolate overflow-hidden bg-ink pb-20 pt-44 text-white md:pb-28 lg:pt-60'
            >
                <div aria-hidden='true' className='starfield absolute inset-0 -z-10 opacity-50' />
                <div
                    aria-hidden='true'
                    className='absolute inset-x-0 bottom-0 -z-10 h-full bg-[radial-gradient(60%_60%_at_70%_100%,rgba(20,130,220,0.4),transparent_75%)]'
                />

                <Container>
                    <SectionLabel tone='dark'>Serviços</SectionLabel>

                    <h1 className='mt-6 max-w-4xl font-heading text-[2.2rem] uppercase leading-[1.02] tracking-[-0.02em] md:text-6xl lg:text-7xl'>
                        Atuação completa em Comércio Exterior
                    </h1>

                    <p className='mt-8 max-w-2xl text-lg leading-relaxed text-white/70'>
                        {servicesIntro}
                    </p>

                    <div className='mt-10 flex flex-wrap gap-3'>
                        <PillLink href={company.simulationUrl} variant='highlight'>
                            Simule agora
                        </PillLink>

                        <PillLink href='/contato' variant='outline-light'>
                            Fale com um especialista
                        </PillLink>
                    </div>
                </Container>
            </section>

            <section id='lista-de-servicos' data-header-theme='light' className='bg-white py-20 md:py-28'>
                <Container>
                    <ol className='border-t'>
                        {services.map((service, index) => {
                            const Icon = service.icon

                            return (
                                <li
                                    key={service.slug}
                                    id={service.slug}
                                    className='grid scroll-mt-28 gap-6 border-b py-10 md:py-14 lg:grid-cols-12 lg:gap-8'
                                >
                                    <div className='flex items-start gap-5 lg:col-span-5'>
                                        <span className='pt-2 font-mono text-[11px] text-muted-foreground'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>

                                        <div>
                                            <span className='flex size-12 items-center justify-center rounded-xl bg-ink text-white'>
                                                <Icon aria-hidden='true' className='size-5' />
                                            </span>

                                            <h2 className='mt-6 font-heading text-2xl uppercase leading-[1.08] tracking-[-0.02em] md:text-[2rem]'>
                                                {service.name}
                                            </h2>
                                        </div>
                                    </div>

                                    <div className='flex flex-col gap-8 lg:col-span-6 lg:col-start-7'>
                                        <p className='text-lg leading-relaxed text-muted-foreground'>
                                            {service.summary}
                                        </p>

                                        {service.items && service.items.length > 0 && (
                                            <ul className='grid gap-2 sm:grid-cols-2'>
                                                {service.items.map((item) => (
                                                    <li
                                                        key={item}
                                                        className='flex items-start gap-3 rounded-xl bg-secondary px-4 py-3 text-sm leading-snug'
                                                    >
                                                        <span aria-hidden='true' className='mt-1.5 size-1.5 shrink-0 bg-brand' />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        <PillLink
                                            href={`/contato?servico=${encodeURIComponent(service.name)}#formulario`}
                                            variant='outline-dark'
                                            size='sm'
                                            className='self-start'
                                        >
                                            Solicitar atendimento
                                        </PillLink>
                                    </div>
                                </li>
                            )
                        })}
                    </ol>
                </Container>
            </section>

            <section id='beneficios' data-header-theme='light' className='scroll-mt-20 bg-secondary py-24 md:py-32'>
                <Container>
                    <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                        <div className='lg:col-span-7'>
                            <SectionLabel>Benefícios</SectionLabel>

                            <h2 className='reveal mt-6 font-heading text-[2.1rem] uppercase leading-[1.04] tracking-[-0.02em] md:text-5xl lg:text-[3.4rem]'>
                                {benefitsTitle}
                            </h2>
                        </div>
                    </div>

                    <ul className='mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                        {benefits.map((benefit) => {
                            const Icon = benefit.icon

                            return (
                                <li key={benefit.title} className='flex flex-col gap-10 rounded-3xl bg-white p-7'>
                                    <span className='flex size-10 items-center justify-center rounded-lg bg-brand text-white'>
                                        <Icon aria-hidden='true' className='size-4' />
                                    </span>

                                    <div>
                                        <h3 className='font-heading text-base uppercase leading-snug tracking-[-0.01em]'>
                                            {benefit.title}
                                        </h3>

                                        <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                                            {benefit.text}
                                        </p>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </Container>
            </section>

            <Cta />
        </Layout>
    )
}

export default Services
